<template>
    <v-container class="px-5">
        <v-row>
            <v-col>
                <h4>Device Trace Logs</h4>
                <p class="text-caption text-grey">
                    Diagnostic breadcrumbs uploaded by a device, one archive per day. Used to work out why a phone
                    stopped reporting when there is nothing on the server to look at. These files describe a person's
                    movements, so they are visible to super admins only and are deleted after 30 days.
                </p>
            </v-col>
        </v-row>

        <v-row dense>
            <v-col cols="12" md="4">
                <v-text-field v-model="deviceSearch" density="compact" label="Search device ID or serial"
                              prepend-inner-icon="mdi-magnify" clearable variant="outlined" hide-details
                              @update:model-value="debouncedFetch"/>
            </v-col>
            <v-col cols="6" md="3">
                <v-text-field v-model="from" type="date" density="compact" label="Captured from"
                              variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
            <v-col cols="6" md="3">
                <v-text-field v-model="to" type="date" density="compact" label="Captured to"
                              variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn variant="tonal" size="small" prepend-icon="mdi-refresh" @click="resetFetch">Refresh</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table-server v-model:items-per-page="perPage" v-model:page="page"
                                     :headers="headers" :items="logs" :items-length="totalItems"
                                     :loading="loading" :items-per-page-options="[25, 50, 100]"
                                     @update:options="fetchLogs">
                    <template v-slot:item.captured_on="{ item }">
                        <span class="text-no-wrap">{{ formatDay(item.captured_on) }}</span>
                    </template>
                    <template v-slot:item.segment_started_at="{ item }">
                        <span class="text-no-wrap">{{ formatClock(item.segment_started_at) }}</span>
                    </template>
                    <template v-slot:item.device_id="{ item }">
                        <code>{{ item.device_id }}</code>
                    </template>
                    <template v-slot:item.slno="{ item }">
                        <span v-if="item.slno">{{ item.slno }}</span>
                        <span v-else class="text-caption text-grey">-</span>
                    </template>
                    <template v-slot:item.size_bytes="{ item }">
                        <span class="text-no-wrap">{{ formatSize(item.size_bytes) }}</span>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                        <span class="text-no-wrap">{{ formatDate(item.created_at) }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn size="x-small" variant="text" icon="mdi-file-tree" title="Show contents"
                               @click="openContents(item)"/>
                        <v-btn size="x-small" variant="text" icon="mdi-download" title="Download archive"
                               :loading="downloadingId === item.id" @click="downloadArchive(item)"/>
                        <v-btn size="x-small" variant="text" icon="mdi-delete" color="error" title="Delete archive"
                               :loading="deletingId === item.id" @click="confirmDelete(item)"/>
                    </template>
                    <template v-slot:no-data>
                        <div class="text-caption text-grey pa-4">No device logs have been uploaded for this filter.</div>
                    </template>
                </v-data-table-server>
            </v-col>
        </v-row>

        <v-dialog v-model="deleteDialog" max-width="440">
            <v-card>
                <v-card-title class="text-subtitle-1">Delete this archive?</v-card-title>
                <v-card-text class="text-body-2">
                    The trace log for device {{ deleteTarget?.device_id }} on
                    {{ formatDay(deleteTarget?.captured_on) }} will be removed from the server. This cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
                    <v-btn color="error" variant="tonal" @click="doDelete">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="contentsDialog" max-width="900">
            <v-card>
                <v-card-title class="text-subtitle-1">
                    {{ contents?.filename || 'Archive contents' }}
                    <div class="text-caption text-grey">
                        Device {{ contents?.device_id }}
                        <span v-if="contents?.segment_started_at">
                            &middot; segment {{ formatClock(contents.segment_started_at) }}
                        </span>
                        <span v-if="contents?.slno">({{ contents.slno }})</span>
                        &middot; {{ formatDay(contents?.captured_on) }}
                    </div>
                </v-card-title>
                <v-divider/>
                <v-card-text>
                    <v-progress-linear v-if="contentsLoading" indeterminate/>
                    <v-alert v-else-if="contents && !contents.entries.length" type="info" variant="tonal" density="compact">
                        The archive is empty.
                    </v-alert>
                    <v-table v-else-if="contents" density="compact">
                        <thead>
                        <tr>
                            <th>File</th>
                            <th class="text-right">Size</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="entry in contents.entries" :key="entry.index">
                            <td><code>{{ entry.name }}</code></td>
                            <td class="text-right text-no-wrap">{{ formatSize(entry.size_bytes) }}</td>
                            <td class="text-right">
                                <v-btn size="x-small" variant="text" prepend-icon="mdi-eye"
                                       :loading="viewingEntry === entry.index" @click="viewEntry(entry)">View</v-btn>
                            </td>
                        </tr>
                        </tbody>
                    </v-table>
                    <v-alert v-if="contents?.truncated" type="warning" variant="tonal" density="compact" class="mt-3">
                        This archive holds more files than are listed here. Download it to see them all.
                    </v-alert>

                    <div v-if="entryText !== null" class="mt-4">
                        <div class="d-flex align-center mb-1">
                            <strong class="text-caption">{{ entryName }}</strong>
                            <v-spacer/>
                            <v-btn size="x-small" variant="text" icon="mdi-close" @click="entryText = null"/>
                        </div>
                        <pre class="log-view">{{ entryText }}</pre>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-btn v-if="contents" variant="tonal" size="small" prepend-icon="mdi-download"
                           :loading="downloadingId === contents.id" @click="downloadArchive(contents)">
                        Download archive
                    </v-btn>
                    <v-spacer/>
                    <v-btn variant="text" @click="contentsDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {ref} from 'vue'
import {send} from '@/composables/apiClient'
import {useSnackbarStore} from '@/stores/snackBarStore'

const snackbar = useSnackbarStore()

const loading = ref(false)
const logs = ref([])
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(25)
const deviceSearch = ref('')
const from = ref(null)
const to = ref(null)

const contentsDialog = ref(false)
const contentsLoading = ref(false)
const contents = ref(null)
const entryText = ref(null)
const entryName = ref('')
const viewingEntry = ref(null)
const downloadingId = ref(null)
const deletingId = ref(null)
const deleteTarget = ref(null)
const deleteDialog = ref(false)

let debounceTimer = null

const headers = [
    {title: 'Captured on', key: 'captured_on'},
    {title: 'Segment', key: 'segment_started_at', sortable: false},
    {title: 'Device ID', key: 'device_id', sortable: false},
    {title: 'Serial', key: 'slno', sortable: false},
    {title: 'Archive', key: 'filename', sortable: false},
    {title: 'Size', key: 'size_bytes', sortable: false, align: 'end'},
    {title: 'Uploaded', key: 'created_at', sortable: false},
    {title: '', key: 'actions', sortable: false, align: 'end'},
]

const formatDay = (value) => {
    if (!value) return '-'
    // captured_on is a plain calendar day. Parsing it through Date() would shift
    // it by the browser's offset and show the previous day west of UTC, so the
    // date part is taken verbatim.
    return String(value).slice(0, 10)
}

const formatDate = (value) => {
    if (!value) return '-'
    return new Date(value).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
}

// The device's OWN local time, rendered as sent. Passing it through Date()
// would re-express it in the viewer's zone, which is the opposite of what is
// wanted: the question is always what the clock said where the phone was.
const formatClock = (value) => {
    if (!value) return '-'
    const m = String(value).match(/T(\d{2}:\d{2}:\d{2})/)
    return m ? m[1] : String(value)
}

const formatSize = (bytes) => {
    const n = Number(bytes)
    if (!Number.isFinite(n) || n <= 0) return '0 B'
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

const debouncedFetch = () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
        page.value = 1
        fetchLogs()
    }, 300)
}

const resetFetch = () => {
    page.value = 1
    fetchLogs()
}

const fetchLogs = () => {
    loading.value = true
    send('get', 'devices/logs', {
        page: page.value,
        per_page: perPage.value,
        device: deviceSearch.value || undefined,
        from: from.value || undefined,
        to: to.value || undefined,
    }).then((response) => {
        const data = response.data.data
        logs.value = data.data ?? []
        totalItems.value = data.total ?? 0
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to load device logs', 'error')
    }).finally(() => loading.value = false)
}

const openContents = (item) => {
    contents.value = null
    entryText.value = null
    contentsDialog.value = true
    contentsLoading.value = true
    send('get', `devices/logs/${item.id}`).then((response) => {
        contents.value = response.data.data
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to read the archive', 'error')
        contentsDialog.value = false
    }).finally(() => contentsLoading.value = false)
}

const viewEntry = (entry) => {
    // Addressed by index, not by name. A zip entry name is raw bytes with no
    // encoding guarantee, so it does not reliably survive a round trip; the
    // index is stable and is what the API resolves against.
    viewingEntry.value = entry.index
    entryText.value = null
    send('get', `devices/logs/${contents.value.id}/download`, {index: entry.index},
        {responseType: 'text'}).then((response) => {
        entryName.value = entry.name
        entryText.value = typeof response.data === 'string'
            ? response.data
            : JSON.stringify(response.data, null, 2)
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to read that file', 'error')
    }).finally(() => viewingEntry.value = null)
}

const confirmDelete = (item) => {
    deleteTarget.value = item
    deleteDialog.value = true
}

const doDelete = () => {
    const item = deleteTarget.value
    deleteDialog.value = false
    deletingId.value = item.id
    send('delete', `devices/logs/${item.id}`).then(() => {
        snackbar.showSnackbar('Device log deleted', 'success')
        if (contents.value?.id === item.id) contentsDialog.value = false
        fetchLogs()
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to delete the archive', 'error')
    }).finally(() => deletingId.value = null)
}

const downloadArchive = (item) => {
    downloadingId.value = item.id
    send('get', `devices/logs/${item.id}/download`, null, {responseType: 'blob'}).then((res) => {
        const disposition = res.headers['content-disposition'] || ''
        const match = disposition.match(/filename="?([^";]+)"?/)
        const filename = match ? match[1] : `device-log-${item.id}.zip`
        const blobUrl = window.URL.createObjectURL(res.data)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
    }).catch((error) => {
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to download the archive', 'error')
    }).finally(() => downloadingId.value = null)
}
</script>

<style scoped>
.log-view {
    max-height: 320px;
    overflow: auto;
    background: rgba(127, 127, 127, 0.08);
    padding: 8px;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
